import React, { useState, useEffect } from 'react';
import { Loader2, Plus, BookOpen, Edit3, Copy, Trash2, Check, X, ArrowRight, Trophy, ChevronDown, ChevronUp, Save } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VERTICAL_OPTIONS = [
  { value: '', label: '— Any —' },
  { value: 'accounting_finance', label: 'Accounting & Finance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'logistics_supply_chain', label: 'Logistics & Supply Chain' },
  { value: 'hr_compliance', label: 'HR & Compliance' },
];
const VERTICAL_LABELS = Object.fromEntries(VERTICAL_OPTIONS.filter(v => v.value).map(v => [v.value, v.label]));
const SENIORITY_OPTIONS = ['', 'entry', 'junior', 'mid', 'senior', 'manager', 'director', 'vp'];

const token = () => localStorage.getItem('backoffice_token');

// ── Playbook Card ──
const PlaybookCard = ({ pb, onApply, onEdit, onDuplicate, onDelete, bestId }) => {
  const [expanded, setExpanded] = useState(false);
  const perf = pb.performance || {};
  const isBest = pb.playbook_id === bestId && perf.times_used > 0;
  const crit = pb.criteria || {};

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${isBest ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-gray-200'}`}
      data-testid={`playbook-${pb.playbook_id}`}>
      <div className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 cursor-pointer" onClick={() => setExpanded(s => !s)}>
        <div className="flex items-center gap-2 min-w-0">
          <BookOpen className="h-3.5 w-3.5 text-[#D4AF37] flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold text-[#0A1F44] truncate">{pb.name}</span>
              <span className="text-[8px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">v{pb.version}</span>
              {pb.vertical && <span className="text-[7px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">{VERTICAL_LABELS[pb.vertical] || pb.vertical}</span>}
              {isBest && <span className="flex items-center gap-0.5 text-[7px] text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full font-bold" data-testid={`playbook-best-${pb.playbook_id}`}><Trophy className="h-2 w-2" />Top</span>}
            </div>
            {pb.description && <p className="text-[9px] text-gray-400 truncate">{pb.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          {perf.times_used > 0 && (
            <div className="text-right mr-2">
              <span className={`text-[10px] font-bold ${perf.overall_avg >= 50 ? 'text-emerald-600' : perf.overall_avg >= 30 ? 'text-amber-600' : 'text-gray-500'}`}>{perf.overall_avg} avg</span>
              <p className="text-[7px] text-gray-400">{perf.times_used}x used</p>
            </div>
          )}
          <button onClick={(e) => { e.stopPropagation(); onApply(pb); }}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-[#0A1F44] text-white text-[9px] font-bold rounded-md hover:bg-[#0d2a5c]"
            data-testid={`playbook-apply-${pb.playbook_id}`}>
            Apply <ArrowRight className="h-2.5 w-2.5" />
          </button>
          {expanded ? <ChevronUp className="h-3 w-3 text-gray-400" /> : <ChevronDown className="h-3 w-3 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-100 pt-2 space-y-2">
          {/* Criteria */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[8px] text-gray-400 uppercase font-bold mb-0.5">Titles</p>
              <div className="flex flex-wrap gap-1">
                {(crit.search_titles || []).map((t, i) => <span key={i} className="px-1.5 py-0.5 bg-[#0A1F44]/5 text-[#0A1F44] text-[8px] rounded">{t}</span>)}
                {(!crit.search_titles || crit.search_titles.length === 0) && <span className="text-[8px] text-gray-300 italic">None</span>}
              </div>
            </div>
            <div>
              <p className="text-[8px] text-gray-400 uppercase font-bold mb-0.5">Keywords</p>
              <div className="flex flex-wrap gap-1">
                {(crit.title_keywords || []).map((k, i) => <span key={i} className="px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[8px] rounded">{k}</span>)}
                {(!crit.title_keywords || crit.title_keywords.length === 0) && <span className="text-[8px] text-gray-300 italic">None</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            {crit.seniority && <p className="text-[9px] text-gray-500"><strong>Seniority:</strong> {crit.seniority}</p>}
            {crit.location && <p className="text-[9px] text-gray-500"><strong>Location:</strong> {crit.location}</p>}
          </div>

          {/* Performance */}
          {perf.times_used > 0 && (
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="text-[8px] text-gray-400 uppercase font-bold mb-1">Performance</p>
              <div className="flex gap-4 text-[10px]">
                <span>Avg: <strong className={perf.overall_avg >= 50 ? 'text-emerald-600' : perf.overall_avg >= 30 ? 'text-amber-600' : 'text-gray-500'}>{perf.overall_avg}</strong></span>
                <span>High-fit: <strong className="text-emerald-600">{perf.total_high_fit}</strong></span>
                <span>Total: <strong>{perf.total_candidates}</strong></span>
                <span>Used: <strong>{perf.times_used}x</strong></span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-1.5 pt-1">
            <button onClick={() => onEdit(pb)} className="flex items-center gap-1 px-2 py-1 text-[8px] text-gray-500 hover:bg-gray-100 rounded-md" data-testid={`playbook-edit-${pb.playbook_id}`}>
              <Edit3 className="h-2.5 w-2.5" /> Edit
            </button>
            <button onClick={() => onDuplicate(pb.playbook_id)} className="flex items-center gap-1 px-2 py-1 text-[8px] text-gray-500 hover:bg-gray-100 rounded-md" data-testid={`playbook-dup-${pb.playbook_id}`}>
              <Copy className="h-2.5 w-2.5" /> Duplicate
            </button>
            <button onClick={() => onDelete(pb.playbook_id)} className="flex items-center gap-1 px-2 py-1 text-[8px] text-red-400 hover:bg-red-50 rounded-md" data-testid={`playbook-delete-${pb.playbook_id}`}>
              <Trash2 className="h-2.5 w-2.5" /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Create/Edit Form ──
const PlaybookForm = ({ initial, onSave, onCancel }) => {
  const [name, setName] = useState(initial?.name || '');
  const [vertical, setVertical] = useState(initial?.vertical || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [titles, setTitles] = useState((initial?.criteria?.search_titles || []).join(', '));
  const [keywords, setKeywords] = useState((initial?.criteria?.title_keywords || []).join(', '));
  const [seniority, setSeniority] = useState(initial?.criteria?.seniority || '');
  const [location, setLocation] = useState(initial?.criteria?.location || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload = {
      name: name.trim(),
      vertical,
      description,
      search_titles: titles.split(',').map(s => s.trim()).filter(Boolean),
      title_keywords: keywords.split(',').map(s => s.trim()).filter(Boolean),
      seniority,
      location,
    };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div className="p-3 bg-white border border-[#D4AF37]/30 rounded-xl space-y-2.5" data-testid="playbook-form">
      <p className="text-[10px] font-bold text-[#0A1F44] uppercase tracking-wider">{initial ? 'Edit Playbook' : 'New Playbook'}</p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[9px] text-gray-500">Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Logistics Best Practice"
            className="w-full px-2 py-1.5 text-[11px] border border-gray-300 rounded-md focus:ring-1 focus:ring-[#D4AF37] focus:outline-none" data-testid="playbook-form-name" />
        </div>
        <div>
          <label className="text-[9px] text-gray-500">Vertical</label>
          <select value={vertical} onChange={e => setVertical(e.target.value)}
            className="w-full px-2 py-1.5 text-[11px] border border-gray-300 rounded-md focus:ring-1 focus:ring-[#D4AF37] focus:outline-none" data-testid="playbook-form-vertical">
            {VERTICAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[9px] text-gray-500">Description</label>
        <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Proven targeting for..."
          className="w-full px-2 py-1.5 text-[11px] border border-gray-300 rounded-md focus:ring-1 focus:ring-[#D4AF37] focus:outline-none" data-testid="playbook-form-desc" />
      </div>
      <div>
        <label className="text-[9px] text-gray-500">Search Titles (comma-separated)</label>
        <input value={titles} onChange={e => setTitles(e.target.value)} placeholder="Logistics Analyst, Supply Chain Coordinator"
          className="w-full px-2 py-1.5 text-[11px] border border-gray-300 rounded-md focus:ring-1 focus:ring-[#D4AF37] focus:outline-none" data-testid="playbook-form-titles" />
      </div>
      <div>
        <label className="text-[9px] text-gray-500">Keywords (comma-separated)</label>
        <input value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="logistics, analyst, supply chain"
          className="w-full px-2 py-1.5 text-[11px] border border-gray-300 rounded-md focus:ring-1 focus:ring-[#D4AF37] focus:outline-none" data-testid="playbook-form-keywords" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[9px] text-gray-500">Seniority</label>
          <select value={seniority} onChange={e => setSeniority(e.target.value)}
            className="w-full px-2 py-1.5 text-[11px] border border-gray-300 rounded-md focus:ring-1 focus:ring-[#D4AF37] focus:outline-none" data-testid="playbook-form-seniority">
            {SENIORITY_OPTIONS.map(s => <option key={s} value={s}>{s || '— Any —'}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[9px] text-gray-500">Location</label>
          <input value={location} onChange={e => setLocation(e.target.value)} placeholder="Tampa, Florida"
            className="w-full px-2 py-1.5 text-[11px] border border-gray-300 rounded-md focus:ring-1 focus:ring-[#D4AF37] focus:outline-none" data-testid="playbook-form-location" />
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#0A1F44] text-white text-[10px] font-bold rounded-md hover:bg-[#0d2a5c] disabled:opacity-50"
          data-testid="playbook-form-save">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
          {initial ? 'Save Changes' : 'Create Playbook'}
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 text-[10px] text-gray-500 hover:bg-gray-100 rounded-md" data-testid="playbook-form-cancel">Cancel</button>
      </div>
    </div>
  );
};

// ── Main Component ──
export const TargetingPlaybooks = ({ onApplyToRole, onApplyToSimulator }) => {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPb, setEditingPb] = useState(null);

  useEffect(() => { loadPlaybooks(); }, []);

  const loadPlaybooks = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/playbooks`, { headers: { Authorization: `Bearer ${token()}` } });
      const d = await res.json();
      if (d.playbooks) setPlaybooks(d.playbooks);
    } catch { /* silent */ }
    setLoading(false);
  };

  const createPlaybook = async (payload) => {
    try {
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/playbooks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (d.ok) { toast.success(`Playbook "${payload.name}" created`); setShowForm(false); loadPlaybooks(); }
      else toast.error(d.detail || 'Failed');
    } catch { toast.error('Failed to create playbook'); }
  };

  const updatePlaybook = async (payload) => {
    try {
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/playbooks/${editingPb.playbook_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (d.ok) { toast.success(`Playbook updated to v${d.playbook.version}`); setEditingPb(null); loadPlaybooks(); }
      else toast.error(d.detail || 'Failed');
    } catch { toast.error('Failed to update'); }
  };

  const duplicatePlaybook = async (pbId) => {
    try {
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/playbooks/${pbId}/duplicate`, {
        method: 'POST', headers: { Authorization: `Bearer ${token()}` },
      });
      const d = await res.json();
      if (d.ok) { toast.success(`Playbook duplicated: "${d.playbook.name}"`); loadPlaybooks(); }
    } catch { toast.error('Failed to duplicate'); }
  };

  const deletePlaybook = async (pbId) => {
    try {
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/playbooks/${pbId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token()}` },
      });
      const d = await res.json();
      if (d.ok) { toast.success('Playbook deleted'); loadPlaybooks(); }
    } catch { toast.error('Failed to delete'); }
  };

  const handleApply = (pb) => {
    if (onApplyToRole) {
      onApplyToRole(pb);
    } else if (onApplyToSimulator) {
      onApplyToSimulator({
        title: (pb.criteria?.search_titles || [])[0] || '',
        keywords: (pb.criteria?.title_keywords || []).join(', '),
        seniority: pb.criteria?.seniority || '',
        vertical: pb.vertical || '',
        location: pb.criteria?.location || '',
      });
    }
  };

  // Find best playbook
  const bestId = playbooks.reduce((best, pb) => {
    const perf = pb.performance || {};
    if (perf.times_used > 0 && perf.overall_avg > (playbooks.find(p => p.playbook_id === best)?.performance?.overall_avg || 0)) return pb.playbook_id;
    return best;
  }, null);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-3" data-testid="targeting-playbooks">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-[#D4AF37]" />
          <h3 className="text-sm font-bold text-[#0A1F44]">Targeting Playbooks</h3>
          <span className="text-[8px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{playbooks.length}</span>
        </div>
        {!showForm && !editingPb && (
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#C4A030] text-[#0A1F44] text-[9px] font-bold rounded-md hover:shadow-md"
            data-testid="playbook-create-btn">
            <Plus className="h-3 w-3" /> New Playbook
          </button>
        )}
      </div>

      {/* Create Form */}
      {showForm && (
        <PlaybookForm onSave={createPlaybook} onCancel={() => setShowForm(false)} />
      )}

      {/* Edit Form */}
      {editingPb && (
        <PlaybookForm initial={editingPb} onSave={updatePlaybook} onCancel={() => setEditingPb(null)} />
      )}

      {/* Playbook List */}
      {playbooks.length === 0 && !showForm ? (
        <p className="text-[10px] text-gray-400 italic py-4 text-center">No playbooks yet. Create one from proven targeting patterns.</p>
      ) : (
        <div className="space-y-2">
          {playbooks.map(pb => (
            <PlaybookCard
              key={pb.playbook_id}
              pb={pb}
              bestId={bestId}
              onApply={handleApply}
              onEdit={(p) => { setEditingPb(p); setShowForm(false); }}
              onDuplicate={duplicatePlaybook}
              onDelete={deletePlaybook}
            />
          ))}
        </div>
      )}
    </div>
  );
};
